/**
 * Port of the Flutter app's `SessionSummary` (`session_summary.dart`).
 *
 * Every field except `id` is optional: rows written by older app versions may
 * be missing columns, and the UI must degrade rather than crash. A row that
 * cannot be read at all is skipped and counted, never thrown.
 */

export interface SessionSummary {
  readonly id: string;
  readonly userId: string;
  readonly sessionId: string | null;
  readonly contentId: string | null;
  /**
   * The session mode. Newer rows leave the `mode` column empty and carry the
   * same information as `details.category`, so this falls back to that.
   */
  readonly mode: string | null;

  /** Localised titles, e.g. `{ en: 'AI Meditation', fa: '…' }`. */
  readonly titles: Readonly<Record<string, string>>;

  /** Set when `title` was a plain string rather than a localised JSON map. */
  readonly rawTitle: string | null;

  readonly startTime: Date | null;
  readonly endTime: Date | null;
  readonly durationSec: number | null;
  readonly valuesAvg: number | null;
  readonly valuesMin: number | null;
  readonly valuesMax: number | null;
  readonly timelineFileId: string | null;
  readonly rawFileId: string | null;
  readonly createdAt: Date | null;

  /**
   * `values_downsample`: a short preview of the session curve (≈50 points),
   * on the same scale as `values_avg/min/max`. Empty when the row predates it.
   * Note the points are evenly resampled — index ≠ chunk number.
   */
  readonly valuesDownsample: readonly number[];

  /** Parsed `details` JSON, or `null` when absent or unreadable. */
  readonly details: SessionDetails | null;

  readonly deviceId: string | null;
  readonly categoryId: string | null;
}

/** A marker the app played during the session (e.g. a "nudge" sound). */
export interface SessionSoundEvent {
  /** Index into `valuesDownsample`, not a chunk or second offset. */
  readonly dataIndex: number;
  readonly category: string | null;
  readonly iconKey: string | null;
  /**
   * As stored. Observed to be on a ×100 scale relative to `valuesDownsample`
   * (7.617 vs 0.07617) — divide by 100 before plotting on the same axis.
   */
  readonly value: number | null;
}

export interface SessionDetails {
  readonly category: string | null;
  /** Length of one timeline chunk in seconds. */
  readonly chunkSeconds: number | null;
  readonly soundEvents: readonly SessionSoundEvent[];
}

/** Minimal shape of an Appwrite document row this parser accepts. */
export interface SessionRow {
  $id: string;
  $createdAt?: string;
  [key: string]: unknown;
}

// ---------------------------------------------------------------------------
// Coercion helpers — the web equivalents of the Dart `_asString` family.
// ---------------------------------------------------------------------------

function asString(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  const text = typeof value === 'string' ? value : String(value);
  return text.trim() === '' ? null : text;
}

function asInt(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return Math.round(value);
  if (typeof value === 'string') {
    const parsed = Number.parseInt(value.trim(), 10);
    return Number.isNaN(parsed) ? null : parsed;
  }
  return null;
}

function asDouble(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number.parseFloat(value.trim());
    return Number.isNaN(parsed) ? null : parsed;
  }
  return null;
}

/**
 * `start_time` / `end_time` are Unix timestamps. Most rows store **seconds**,
 * but some store **milliseconds** — anything above 1e12 (year ~33658 in
 * seconds) is treated as milliseconds.
 */
function asDate(value: unknown): Date | null {
  const seconds = asInt(value);
  if (seconds === null || seconds <= 0) return null;
  const date = new Date(seconds > 1e12 ? seconds : seconds * 1000);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Decodes a value that may be a JSON string or an already-decoded value. */
function decodeJson(raw: unknown): unknown {
  if (typeof raw !== 'string') return raw;
  if (raw.trim() === '') return null;
  try {
    return JSON.parse(raw) as unknown;
  } catch {
    return null;
  }
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function parseDownsample(raw: unknown): number[] {
  const decoded = decodeJson(raw);
  if (!Array.isArray(decoded)) return [];
  const points: number[] = [];
  for (const item of decoded) {
    const value = asDouble(item);
    if (value !== null) points.push(value);
  }
  return points;
}

function parseDetails(raw: unknown): SessionDetails | null {
  const record = asRecord(decodeJson(raw));
  if (record === null) return null;

  const soundEvents: SessionSoundEvent[] = [];
  const events = record['sound_events'];
  if (Array.isArray(events)) {
    for (const item of events) {
      const event = asRecord(item);
      if (event === null) continue;
      const dataIndex = asInt(event['dataIndex']);
      if (dataIndex === null || dataIndex < 0) continue;
      soundEvents.push({
        dataIndex,
        category: asString(event['category']),
        iconKey: asString(event['iconKey']),
        value: asDouble(event['value']),
      });
    }
  }

  return {
    category: asString(record['category']),
    chunkSeconds: asDouble(record['chunk_seconds']),
    soundEvents,
  };
}

function parseTitles(raw: unknown): { titles: Record<string, string>; rawTitle: string | null } {
  const titles: Record<string, string> = {};
  let rawTitle: string | null = null;

  if (raw !== null && typeof raw === 'object' && !Array.isArray(raw)) {
    for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
      if (value !== null && value !== undefined) titles[key] = String(value);
    }
  } else if (typeof raw === 'string' && raw.trim() !== '') {
    try {
      const decoded: unknown = JSON.parse(raw);
      if (decoded !== null && typeof decoded === 'object' && !Array.isArray(decoded)) {
        for (const [key, value] of Object.entries(decoded as Record<string, unknown>)) {
          if (value !== null && value !== undefined) titles[key] = String(value);
        }
      } else {
        rawTitle = raw;
      }
    } catch {
      // Not JSON — it is simply a plain title string.
      rawTitle = raw;
    }
  }

  return { titles, rawTitle };
}

/**
 * Builds a summary from an Appwrite row. Returns `null` only when the row is
 * unusable, so the caller can count and skip it.
 */
export function sessionSummaryFromRow(row: SessionRow): SessionSummary | null {
  try {
    const id = row.$id;
    if (typeof id !== 'string' || id === '') return null;

    const { titles, rawTitle } = parseTitles(row['title']);
    const details = parseDetails(row['details']);
    const createdAtRaw = typeof row.$createdAt === 'string' ? new Date(row.$createdAt) : null;

    return {
      id,
      userId: asString(row['user_id']) ?? '',
      sessionId: asString(row['session_id']),
      contentId: asString(row['content_id']),
      mode: asString(row['mode']) ?? details?.category ?? null,
      titles,
      rawTitle,
      startTime: asDate(row['start_time']),
      endTime: asDate(row['end_time']),
      durationSec: asInt(row['duration_sec']),
      valuesAvg: asDouble(row['values_avg']),
      valuesMin: asDouble(row['values_min']),
      valuesMax: asDouble(row['values_max']),
      timelineFileId: asString(row['timeline_file_id']),
      rawFileId: asString(row['raw_file_id']),
      createdAt: createdAtRaw && !Number.isNaN(createdAtRaw.getTime()) ? createdAtRaw : null,
      valuesDownsample: parseDownsample(row['values_downsample']),
      details,
      deviceId: asString(row['device_id']),
      categoryId: asString(row['category_id']),
    };
  } catch (error) {
    console.warn(`Skipping malformed session row ${String(row.$id)}:`, error);
    return null;
  }
}

// ---------------------------------------------------------------------------
// Derived values — the Dart getters, as free functions.
// ---------------------------------------------------------------------------

export function hasTimelineFile(session: SessionSummary): boolean {
  return session.timelineFileId !== null && session.timelineFileId.trim() !== '';
}

export function hasRawFile(session: SessionSummary): boolean {
  return session.rawFileId !== null && session.rawFileId.trim() !== '';
}

/** Effective duration: the stored value, or start/end difference as fallback. */
export function effectiveDurationSec(session: SessionSummary): number | null {
  if (session.durationSec !== null && session.durationSec > 0) return session.durationSec;
  if (session.startTime !== null && session.endTime !== null) {
    const diff = Math.floor((session.endTime.getTime() - session.startTime.getTime()) / 1000);
    if (diff > 0) return diff;
  }
  return null;
}

/** Best title for `localeCode`, falling back to English, then any entry. */
export function titleFor(session: SessionSummary, localeCode: string): string {
  const localised = session.titles[localeCode];
  if (localised !== undefined && localised.trim() !== '') return localised;

  const english = session.titles['en'];
  if (english !== undefined && english.trim() !== '') return english;

  const first = Object.values(session.titles)[0];
  if (first !== undefined && first.trim() !== '') return first;

  if (session.rawTitle !== null && session.rawTitle.trim() !== '') return session.rawTitle;
  return 'Untitled session';
}

/**
 * The timestamp a session should be ordered and displayed by.
 *
 * Always `createdAt` (the record's creation time). `start_time` / `end_time`
 * are unreliable — some rows store them in seconds and others in
 * milliseconds — so they are never used for display.
 */
export function sessionTimestamp(session: SessionSummary): Date | null {
  return session.createdAt;
}

/** The identifier shown to the user — the upstream session id when present. */
export function sessionIdentifier(session: SessionSummary): string {
  return session.sessionId ?? session.id;
}
